// Comité de Historia Clínica (2026-09-10, worktree comite-historia-clinica)
// — el Comité es el propio admin del consultorio (decisión de producto
// 2026-09-10, sin rol nuevo). Mismo guard de sesión que AuditoriaPage.tsx/
// UsuariosPage.tsx (leerSesionHceAdmin, NUNCA la de Medplum).
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { notify } from '@/components/ui/sonner';
import { leerSesionHceAdmin } from '@/auth/hceAdminSession';
import {
  CHECKLIST_COMITE,
  decidirCandidatoComite,
  descargarActaComite,
  listarCandidatosComite,
  type CandidatoComite,
} from '@/lib/hceComite';

function PanelDecision({ candidato, token, onDecidido, onFallo }: { candidato: CandidatoComite; token: string; onDecidido: () => void; onFallo: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState<'aprobar' | 'rechazar' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checklistCompleto = CHECKLIST_COMITE.every((_, i) => checklist[String(i)]);

  async function decidir(aprobar: boolean): Promise<void> {
    setEnviando(aprobar ? 'aprobar' : 'rechazar');
    setError(null);
    try {
      const resultado = await decidirCandidatoComite(token, candidato.id, { aprobar, checklist, motivo });
      notify({
        color: 'green',
        message: resultado.estado === 'ejecutado' ? t('comite.decision.ejecutadoOk') : t('comite.decision.rechazadoOk'),
      });
      onDecidido();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Un borrado incompleto (502) ya cambió el estado del candidato en el servidor: se refresca la lista sin cerrar el panel.
      onFallo();
    } finally {
      setEnviando(null);
    }
  }

  return (
    <div className="mt-3 space-y-3 rounded-md border border-border bg-hce-paper p-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-hce-muted">{t('comite.checklist.titulo')}</p>
        <div className="mt-2 space-y-2">
          {CHECKLIST_COMITE.map((item, i) => (
            <label key={i} className="flex items-start gap-2 text-sm text-hce-ink">
              <Checkbox checked={checklist[String(i)] ?? false} onCheckedChange={(v) => setChecklist((c) => ({ ...c, [String(i)]: v === true }))} className="mt-0.5" />
              {item}
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-hce-muted">{t('comite.motivo.titulo')}</p>
        <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={t('comite.motivo.placeholder')} className="mt-1" rows={2} />
      </div>
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex gap-2">
        <Button variant="destructive" disabled={!checklistCompleto || enviando !== null} onClick={() => void decidir(true)}>
          {enviando === 'aprobar' ? t('comite.decision.ejecutando') : t('comite.decision.aprobar')}
        </Button>
        {candidato.estado === 'pendiente' && (
          <Button variant="outline" disabled={enviando !== null} onClick={() => void decidir(false)}>
            {enviando === 'rechazar' ? t('comite.decision.rechazando') : t('comite.decision.rechazar')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function ComitePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sesion = leerSesionHceAdmin();
  const [candidatos, setCandidatos] = useState<CandidatoComite[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandido, setExpandido] = useState<number | null>(null);
  const [descargando, setDescargando] = useState<number | null>(null);

  const cargar = useCallback(async () => {
    if (!sesion) return;
    setCargando(true);
    setError(null);
    try {
      setCandidatos(await listarCandidatosComite(sesion.token));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('comite.errorCargar'));
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function descargarActa(id: number): Promise<void> {
    if (!sesion) return;
    setDescargando(id);
    try {
      await descargarActaComite(sesion.token, id);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
    } finally {
      setDescargando(null);
    }
  }

  if (!sesion) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hce-paper px-4">
        <Alert variant="red" className="max-w-sm">
          <AlertDescription>{t('admin.usuarios.sesionVencida')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // 'parcial'/'fallido' siguen en la lista de pendientes: el borrado no se completó y se reintenta desde aquí.
  const esPendiente = (c: CandidatoComite): boolean => c.estado === 'pendiente' || c.estado === 'parcial' || c.estado === 'fallido';
  const pendientes = (candidatos ?? []).filter(esPendiente);
  const decididos = (candidatos ?? []).filter((c) => !esPendiente(c));

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-medium text-hce-ink">{t('comite.titulo')}</h1>
          <Button variant="outline" size="sm" onClick={() => void navigate('/usuarios')}>
            {t('comite.volver')}
          </Button>
        </div>
        <p className="text-sm text-hce-muted">{t('comite.descripcion')}</p>

        {error && (
          <Alert variant="red">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {cargando && !candidatos ? (
          <p className="text-sm text-hce-muted">{t('comite.cargando')}</p>
        ) : pendientes.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('comite.sinPendientes')}</p>
        ) : (
          <div className="space-y-2">
            {pendientes.map((c) => (
              <div key={c.id} className="rounded-lg border border-border bg-hce-surface p-3">
                <button type="button" className="flex w-full items-center justify-between gap-3 text-left" onClick={() => setExpandido(expandido === c.id ? null : c.id)}>
                  <div>
                    <div className="text-sm font-medium text-hce-ink">{c.nombre}</div>
                    <div className="font-mono text-xs text-hce-muted">
                      {t('comite.cedula')} {c.idNumber} · {t('comite.ultimaAtencion')} {c.ultimaAtencion ?? '—'}
                    </div>
                  </div>
                  <Badge variant={c.estado === 'pendiente' ? 'clay' : 'destructive'}>
                    {c.estado === 'pendiente' ? (expandido === c.id ? t('comite.ocultar') : t('comite.revisar')) : t(`comite.estado.${c.estado}`)}
                  </Badge>
                </button>
                {expandido === c.id && <PanelDecision candidato={c} token={sesion.token} onDecidido={() => { setExpandido(null); void cargar(); }} onFallo={() => void cargar()} />}
              </div>
            ))}
          </div>
        )}

        {decididos.length > 0 && (
          <div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-hce-muted">{t('comite.historial')}</p>
            <div className="mt-2 space-y-1.5">
              {decididos.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-hce-surface px-3 py-2 text-sm">
                  <div>
                    <span className="text-hce-ink">{c.nombre}</span>
                    <span className="ml-2 font-mono text-xs text-hce-muted">{c.idNumber}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={c.estado === 'ejecutado' ? 'destructive' : c.estado === 'rechazado' ? 'outline' : 'sage'}>
                      {t(`comite.estado.${c.estado}`)}
                    </Badge>
                    {c.estado === 'ejecutado' && (
                      <button type="button" className="text-xs text-hce-pine underline-offset-2 hover:underline" disabled={descargando === c.id} onClick={() => void descargarActa(c.id)}>
                        {descargando === c.id ? t('comite.descargando') : t('comite.descargarActa')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
